import { useQueries } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import type { ApiError } from '@/lib/api/client';
import { queryKeys, useApiQuery } from '@/lib/api/queryClient';
import type { UploadResult, ValidationPending, ValidationReport } from '@/lib/api/types';
import { fetchUploadReport, isValidationPending, pollDelayMs } from '@/lib/api/uploads';

/**
 * How many times we ask "is it done yet?" before admitting it is not coming.
 *
 * At the server's default two-second cadence that is about two minutes. The cap
 * exists because an unbounded poll is indistinguishable from a hang: the phone
 * keeps requesting on the customer's data, the screen keeps saying "nearly
 * there", and nobody is ever told to do something else.
 */
export const MAX_POLL_ATTEMPTS = 40;

export interface UploadReportState {
  /** The finished report, once there is one. */
  report: ValidationReport | null;
  /** The 202 body while validation is still running. */
  pending: ValidationPending | null;
  /** How many polls have come back "still validating". */
  attempts: number;
  /** True once we stopped polling without an answer. */
  gaveUp: boolean;
  isLoading: boolean;
  isError: boolean;
  error: ApiError | null;
  /** Re-arms the poll after a give-up, or retries a failed fetch. */
  retry: () => void;
}

/**
 * GET /api/v1/uploads/{id}, polled until the verdict lands.
 *
 * Three rules the 202 path has to follow, all visible below:
 *
 *   1. HONOUR `poll_after_seconds`. The server knows how long its own queue
 *      takes; a client that hammers every 500ms just burns the customer's data
 *      and the API's rate limit.
 *   2. CAP IT, and say so when the cap is hit (`gaveUp`) — with a way to try
 *      again rather than a dead screen.
 *   3. STOP THE MOMENT A REPORT ARRIVES. `refetchInterval` returns false as soon
 *      as the body stops being the pending shape, so a finished upload does not
 *      keep a timer alive in a backgrounded tab.
 */
export interface UseUploadReportOptions {
  /**
   * Polls before giving up. A parameter rather than a constant because the cap
   * is a product decision per screen — the review step wants two patient
   * minutes; a future "did it finish?" widget in the order list would want ten
   * seconds — and because a two-minute default is untestable otherwise.
   */
  maxAttempts?: number;
}

export function useUploadReport(
  uploadId: string | undefined,
  { maxAttempts = MAX_POLL_ATTEMPTS }: UseUploadReportOptions = {},
): UploadReportState {
  const [attempts, setAttempts] = useState(0);
  const gaveUp = attempts >= maxAttempts;

  const query = useApiQuery<UploadResult, ReturnType<typeof queryKeys.upload>>({
    queryKey: queryKeys.upload(uploadId ?? ''),
    queryFn: ({ signal }) => fetchUploadReport(uploadId ?? '', signal),
    enabled: Boolean(uploadId),
    // A validation report is immutable once written, so there is nothing to
    // re-fetch after it lands; while it is pending, refetchInterval drives us.
    staleTime: Infinity,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || !isValidationPending(data)) return false;
      if (gaveUp) return false;
      return pollDelayMs(data);
    },
    // Keep polling if the customer backgrounds the tab mid-validation; the whole
    // point is that they can come back to a finished report.
    refetchIntervalInBackground: false,
  });

  const data = query.data;
  const pending = data && isValidationPending(data) ? data : null;
  const report = data && !isValidationPending(data) ? data : null;

  // One increment per completed fetch that came back "still validating".
  // Keyed on `dataUpdatedAt` because that is the value TanStack Query changes
  // exactly once per successful fetch, even when the body is byte-identical.
  const dataUpdatedAt = query.dataUpdatedAt;
  const stillValidating = pending !== null;
  useEffect(() => {
    if (!stillValidating) return;
    setAttempts((previous) => previous + 1);
  }, [dataUpdatedAt, stillValidating]);

  const refetch = query.refetch;
  const retry = useCallback(() => {
    setAttempts(0);
    void refetch();
  }, [refetch]);

  return {
    report,
    pending,
    attempts,
    gaveUp: gaveUp && pending !== null,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    retry,
  };
}

/**
 * Reports for every document in a basket, in basket order.
 *
 * `useQueries` rather than a hook per id, because the number of documents is
 * the customer's choice and React's hook order is not negotiable. Each entry
 * shares the cache with `useUploadReport`, so a report already fetched on the
 * Review step is not fetched again here.
 *
 * Deliberately does NOT poll: by the time a basket reaches checkout every file
 * has been through Review, and a report is immutable once written. A file still
 * validating shows as `report: null`, which the caller reads as "not ready".
 */
export function useUploadReports(uploadIds: readonly string[]): {
  uploadId: string;
  report: ValidationReport | null;
}[] {
  const results = useQueries({
    queries: uploadIds.map((uploadId) => ({
      queryKey: queryKeys.upload(uploadId),
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchUploadReport(uploadId, signal),
      enabled: Boolean(uploadId),
      staleTime: Infinity,
    })),
  });

  return uploadIds.map((uploadId, index) => {
    const data = results[index]?.data;
    return {
      uploadId,
      report: data && !isValidationPending(data) ? data : null,
    };
  });
}
